// DESCRIPTION: Step progress indicator for the multi-step booking wizard.
// DESCRIPTION: Renders numbered circles with connecting lines; marks done/active/pending states.

import React from 'react';
import { __ } from '@wordpress/i18n';

/**
 * StepIndicator — horizontal progress header for the booking wizard.
 *
 * @param {Object}   props
 * @param {string[]} props.steps   Ordered step labels.
 * @param {number}   props.current Zero-based index of the active step.
 *
 * @return {JSX.Element} The step indicator.
 */
const StepIndicator = ( { steps, current } ) => {
	return (
		<div
			className="nb-steps"
			role="list"
			aria-label={ __( 'Booking progress', 'nemtly-booking' ) }
		>
			{ steps.map( ( label, i ) => {
				const isDone = i < current;
				const isActive = i === current;
				// Build literal class names (not interpolated) so Tailwind's
				// content scanner keeps these `@layer components` rules instead
				// of purging them as unused.
				let circleClass = 'nb-steps__circle';
				let labelClass = 'nb-steps__label';
				if ( isDone ) {
					circleClass += ' nb-steps__circle--done';
					labelClass += ' nb-steps__label--done';
				} else if ( isActive ) {
					circleClass += ' nb-steps__circle--active';
					labelClass += ' nb-steps__label--active';
				}

				return (
					<React.Fragment key={ label }>
						<div className="nb-steps__item" role="listitem">
							<span
								className={ circleClass }
								aria-current={ isActive ? 'step' : undefined }
							>
								{ isDone ? '✓' : i + 1 }
							</span>
							<span className={ labelClass }>{ label }</span>
						</div>
						{ i < steps.length - 1 && (
							<span
								className={ `nb-steps__line ${
									isDone ? 'nb-steps__line--done' : ''
								}`.trim() }
								aria-hidden="true"
							/>
						) }
					</React.Fragment>
				);
			} ) }
		</div>
	);
};

export default StepIndicator;
